import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  Divider,
  useMantineColorScheme,
  Box,
} from "@mantine/core"
import { IconGavel, IconCalendar, IconUsers, IconFileText, IconExternalLink, IconClock } from "@tabler/icons-react"
import { TransparencyBadge, TransparencyWarning } from "@/components/ui/TransparencyBadge"
import { SEO } from "@/components/seo/SEO"
import { PageCTASection } from "@/components/ui/PageCTASection"

interface LegislativeEvent {
  date: string
  title: string
  type: string
  result: string
  status: string
  description: string
  details: string[]
  significance: string
  transparencyLevel?: "partial" | "manipulated" | "authentic"
  warningPercentage?: number
  warningNewInfo?: number
  warningDetails?: string
  whatsNeeded?: string
  urgencyLevel?: "critical" | "blocked" | "normal"
}

export const Route = createFileRoute("/legislative")({
  component: LegislativePage,
})

function LegislativePage() {
  const { colorScheme } = useMantineColorScheme()

  const legislativeEvents: LegislativeEvent[] = [
    {
      date: "2026-01-06",
      title: "DOJ Admits Less Than 1% of Files Released",
      type: "DOJ Report",
      result: "12,285 of 2+ Million",
      status: "pending",
      description:
        "Justice Department court filing reveals only 12,285 documents released - less than 1% of the total. Over 2 million documents still being reviewed, with 5.2 million pages total.",
      details: [
        "Only 12,285 documents released so far (less than 1%)",
        "5.2 million pages of Epstein-related documents still to review",
        "400 lawyers from multiple DOJ divisions being recruited",
        "Rep. Garcia (D): Incomplete release 'is absolutely breaking the law'",
        "Rep. Massie (R): Partial release does not comply with the law",
        "Rep. Khanna: FBI interview memorandums with survivor testimony still unreleased",
      ],
      significance: "high",
      transparencyLevel: "partial" as const,
      warningDetails:
        "DOJ missed the 30-day legal deadline. Bipartisan criticism of incomplete compliance with the Epstein Files Transparency Act.",
      whatsNeeded: "Pressure DOJ for full compliance with the law",
      urgencyLevel: "critical",
    },
    {
      date: "2025-12-24",
      title: "DOJ Announces 1+ Million Additional Documents Found",
      type: "DOJ Announcement",
      result: "Over 1 Million More Documents",
      status: "pending",
      description:
        "On Christmas Eve, DOJ announced its Manhattan office and FBI 'uncovered over a million more documents potentially related to the Jeffrey Epstein case.'",
      details: [
        "Manhattan office and FBI found additional documents",
        "Announced on Christmas Eve 2025",
        "Adds to the already massive backlog of unreleased files",
        "Raises questions about completeness of prior searches",
      ],
      significance: "medium",
    },
    {
      date: "2025-12-19",
      title: "DOJ Begins Releasing Epstein Files",
      type: "Document Release",
      result: "Initial Release with Heavy Redactions",
      status: "completed",
      description:
        "DOJ releases first batch of Epstein files as required by law. However, 119 pages of grand jury materials were completely redacted, with many other pages heavily redacted.",
      details: [
        "First official release under Epstein Files Transparency Act",
        "Files include over 300 GB of data, papers, videos, photos, and audio",
        "119 pages of grand jury materials completely redacted",
        "Many other documents heavily redacted",
        "House Oversight Committee separately released 68 photos from ~95,000 obtained from Epstein's estate",
      ],
      significance: "high",
      transparencyLevel: "partial" as const,
      warningDetails:
        "Heavy redactions and incomplete release despite legal requirement for transparency. Grand jury materials still withheld.",
    },
    {
      date: "2025-11-19",
      title: "🎉 Epstein Files Transparency Act Signed Into Law",
      type: "Bill Signed",
      result: "ENACTED",
      status: "passed",
      description:
        "President Trump signs the Epstein Files Transparency Act (H.R.4405) into law, requiring DOJ to release all Epstein-related documents within 30 days.",
      details: [
        "Historic bipartisan achievement for transparency",
        "Law requires Attorney General to release all unclassified records",
        "Documents must be made available in searchable, downloadable format",
        "30-day deadline for full compliance (December 19, 2025)",
        "First successful use of discharge petition in 90 years",
      ],
      significance: "high",
    },
    {
      date: "2025-11-19",
      title: "Senate Passes Epstein Files Transparency Act Unanimously",
      type: "Senate Vote",
      result: "Passed by Unanimous Consent",
      status: "passed",
      description:
        "Senate Minority Leader Chuck Schumer requested unanimous consent to pass the bill. Not a single senator objected, passing it to the President's desk.",
      details: [
        "Passed via unanimous consent - no objections",
        "No amendments or changes to House version",
        "Speaker Johnson and GOP leadership had urged changes but were overruled",
        "Dramatic reversal from September's 51-49 defeat of Schumer amendment",
        "Public pressure and bipartisan coalition prevailed",
      ],
      significance: "high",
    },
    {
      date: "2025-11-18",
      title: "House Passes Epstein Files Transparency Act 427-1",
      type: "House Vote",
      result: "Passed 427-1",
      status: "passed",
      description:
        "The House of Representatives votes overwhelmingly to pass the Epstein Files Transparency Act. Rep. Clay Higgins (R-LA) casts the only 'Nay' vote.",
      details: [
        "Historic near-unanimous vote: 427-1",
        "Only Rep. Clay Higgins (R-LA) voted against",
        "Bipartisan coalition achieved rare legislative victory",
        "Bill compels DOJ to release all Epstein investigative records",
        "Speaker Johnson considering rule changes to limit future discharge petitions",
      ],
      significance: "high",
    },
    {
      date: "2025-11-12",
      title: "🎉 Discharge Petition Reaches 218 Signatures!",
      type: "House Resolution",
      result: "218/218 - SUCCESS",
      status: "passed",
      description:
        "Newly sworn-in Rep. Adelita Grijalva (D-AZ) signs the discharge petition, becoming the 218th signature needed to force a House floor vote on H.Res.577.",
      details: [
        "Rep. Adelita Grijalva (D-AZ) provided the crucial 218th signature",
        "Signed immediately after being sworn in to fill vacant seat",
        "Most successful use of discharge petition in 90 years",
        "Forced Speaker Johnson to bring bill to floor vote",
        "Bipartisan coalition of Democrats and transparency-supporting Republicans",
      ],
      significance: "high",
    },
    {
      date: "2025-09-13",
      title: "House Committee Releases 33,295 Pages of DOJ Records",
      type: "Document Release",
      result: "97% Previously Public",
      status: "completed",
      description:
        "House Oversight Committee releases massive tranche of Epstein-related records from DOJ, including new flight logs from 2000-2014 kept by Customs and Border Protection.",
      details: [
        "33,295 pages released following Chairman Comer's August subpoena",
        "Only 3% of documents contain new information not previously public",
        "Includes previously unseen flight logs and passenger manifests",
        "Committee expanding investigation after victim meetings",
      ],
      significance: "high",
      transparencyLevel: "partial" as const,
      warningPercentage: 97,
      warningNewInfo: 3,
    },
    {
      date: "2025-09-10",
      title: "Senate Defeats Initial Amendment to Release Files",
      type: "Senate Vote",
      result: "Defeated 51-49",
      status: "failed",
      description:
        "Chuck Schumer's amendment (S.Amdt.3849) to compel DOJ release of all Epstein files was tabled by Republican majority. Later reversed with unanimous passage in November.",
      details: [
        "Republicans Against: 49 senators (blocked transparency)",
        "Republicans For: Rand Paul (KY), Josh Hawley (MO)",
        "Democrats For: All 47 senators + 2 Independents",
        "Vote: 51 to table (kill) vs 49 to proceed",
        "Public outcry and discharge petition success led to reversal in November",
      ],
      significance: "medium",
    },
    {
      date: "2025-07-07",
      title: "DOJ Releases Investigation Findings Memo",
      type: "DOJ Report",
      result: "No Client List Found",
      status: "completed",
      description:
        "Department of Justice releases memo stating exhaustive review found no evidence of Epstein 'client list' or blackmail activities, confirming suicide finding.",
      details: [
        "No credible evidence Epstein blackmailed prominent individuals",
        "FBI confirmed death by suicide after thorough investigation",
        "No incriminating client list discovered",
        "Findings based on comprehensive review of evidence",
      ],
      significance: "medium",
      transparencyLevel: "manipulated" as const,
      warningDetails:
        "Convenient findings released by Trump administration's DOJ - highly suspicious timing and conclusions",
    },
    {
      date: "2025-01-20",
      title: "AG Bondi Releases First Phase of Declassified Files",
      type: "Document Release",
      result: "First DOJ Release",
      status: "completed",
      description:
        "Attorney General Pamela Bondi releases first phase of declassified Epstein files, following Trump's transparency commitment regarding Epstein's exploitation of over 250 underage girls.",
      details: [
        "Files detail sexual exploitation at NY and Florida homes",
        "Part of President Trump's commitment to transparency",
        "Released in conjunction with FBI",
        "Focus on Epstein and co-conspirators' actions",
      ],
      significance: "high",
      transparencyLevel: "partial" as const,
      warningDetails:
        "'First phase only' suggests selective release by Trump administration - what are they hiding in later phases?",
    },
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case "passed":
        return "green"
      case "failed":
        return "red"
      case "pending":
        return "yellow"
      default:
        return "gray"
    }
  }

  const getSignificanceColor = (significance: string) => {
    switch (significance) {
      case "high":
        return "red"
      case "medium":
        return "yellow"
      case "low":
        return "blue"
      default:
        return "gray"
    }
  }

  return (
    <>
      <SEO
        title="Legislative Action Tracker"
        description="Track congressional efforts to release Jeffrey Epstein files through legislative action and transparency measures. Monitor votes, resolutions, and document releases."
        canonical="https://rtefn.com/legislative"
      />
      <Box
        style={{
          background:
            colorScheme === "dark"
              ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)"
              : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py="xl">
          {/* Header */}
          <Stack ta="center" mb="xl">
            <Group justify="center" mb="md">
              <IconGavel size={48} style={{ color: "var(--mantine-primary-color-filled)" }} />
            </Group>

            <Title order={1} size="3rem" fw={700} ta="center" mb="md" style={{ letterSpacing: "-0.02em" }}>
              Legislative Action Tracker
            </Title>

            <Text size="xl" ta="center" c="dimmed" maw={600} mx="auto" lh={1.6}>
              Congressional efforts to release Jeffrey Epstein files through legislative action and transparency
              measures.
            </Text>
          </Stack>

          {/* Summary Stats */}
          <Grid mb="xl">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.05)",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconGavel size={32} color="#10b981" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="green.6">
                  ✓ LAW
                </Text>
                <Text size="sm" ta="center" c="dimmed">
                  Epstein Files Transparency Act
                </Text>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconFileText size={32} color="#ef4444" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="red.6">
                  {"<"}1%
                </Text>
                <Text size="sm" ta="center" c="dimmed">
                  DOJ Files Released (Breaking Law)
                </Text>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="lg"
                style={{
                  background: colorScheme === "dark" ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <Group justify="center" mb="sm">
                  <IconUsers size={32} color="#3b82f6" />
                </Group>
                <Text size="2rem" fw={700} ta="center" c="blue.6">
                  2M+
                </Text>
                <Text size="sm" ta="center" c="dimmed">
                  Documents Still Unreleased
                </Text>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Legislative Events */}
          <Stack gap="xl">
            {legislativeEvents.map((event, index) => (
              <Card
                key={index}
                shadow="lg"
                radius="xl"
                p="xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Event Header */}
                <Group justify="space-between" mb="md">
                  <Group>
                    <IconCalendar size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
                    <Text fw={600}>
                      {new Date(event.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Text>
                  </Group>

                  <Group gap="xs">
                    <Badge color={getStatusColor(event.status)} variant="light">
                      {event.result}
                    </Badge>
                    <Badge color={getSignificanceColor(event.significance)} variant="outline">
                      {event.significance} impact
                    </Badge>
                  </Group>
                </Group>

                {/* Event Content */}
                <Title order={3} mb="sm">
                  {event.title}
                </Title>

                <Group mb="md" gap="xs">
                  <Badge color="blue" variant="filled">
                    {event.type}
                  </Badge>
                  {event.transparencyLevel && (
                    <TransparencyBadge level={event.transparencyLevel} details={event.warningDetails} />
                  )}
                  {event.warningPercentage && event.warningNewInfo && (
                    <TransparencyWarning percentage={event.warningPercentage} newInfo={event.warningNewInfo} />
                  )}
                </Group>

                <Text mb="lg" c="dimmed" lh={1.6}>
                  {event.description}
                </Text>

                {/* Event Details */}
                <Divider mb="md" color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />

                <Stack gap="xs">
                  {event.details.map((detail, detailIndex) => (
                    <Group key={detailIndex} gap="sm">
                      <Text size="sm" c="dimmed">
                        •
                      </Text>
                      <Text size="sm" c="dimmed">
                        {detail}
                      </Text>
                    </Group>
                  ))}
                </Stack>

                {/* What's Needed Section */}
                {event.whatsNeeded && (
                  <>
                    <Divider
                      mt="md"
                      mb="md"
                      color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}
                    />
                    <Card
                      p="md"
                      style={{
                        background:
                          event.urgencyLevel === "critical"
                            ? colorScheme === "dark"
                              ? "rgba(245, 158, 11, 0.1)"
                              : "rgba(245, 158, 11, 0.05)"
                            : event.urgencyLevel === "blocked"
                              ? colorScheme === "dark"
                                ? "rgba(239, 68, 68, 0.1)"
                                : "rgba(239, 68, 68, 0.05)"
                              : colorScheme === "dark"
                                ? "rgba(59, 130, 246, 0.1)"
                                : "rgba(59, 130, 246, 0.05)",
                        border:
                          event.urgencyLevel === "critical"
                            ? "1px solid rgba(245, 158, 11, 0.3)"
                            : event.urgencyLevel === "blocked"
                              ? "1px solid rgba(239, 68, 68, 0.3)"
                              : "1px solid rgba(59, 130, 246, 0.3)",
                      }}
                    >
                      <Group gap="sm">
                        <Text
                          size="sm"
                          fw={600}
                          c={
                            event.urgencyLevel === "critical"
                              ? "orange.6"
                              : event.urgencyLevel === "blocked"
                                ? "red.6"
                                : "blue.6"
                          }
                        >
                          What's Needed:
                        </Text>
                        <Text size="sm" fw={500}>
                          {event.whatsNeeded}
                        </Text>
                      </Group>
                    </Card>
                  </>
                )}
              </Card>
            ))}
          </Stack>

          {/* Call to Action */}
          <Card
            shadow="lg"
            radius="xl"
            p="xl"
            mt="xl"
            style={{
              background:
                colorScheme === "dark"
                  ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)"
                  : "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%)",
              border: colorScheme === "dark" ? "1px solid rgba(239, 68, 68, 0.2)" : "1px solid rgba(239, 68, 68, 0.1)",
            }}
          >
            <Stack ta="center">
              <Title order={3} mb="md">
                DOJ Is Breaking the Law - Demand Full Compliance
              </Title>

              <Text mb="lg" c="dimmed" maw={600} mx="auto" lh={1.6}>
                The Epstein Files Transparency Act is now law, but DOJ has released less than 1% of documents. Contact
                your representatives to demand the Attorney General fully comply with the law and release ALL files.
              </Text>

              <Group justify="center" gap="md">
                <Button
                  size="lg"
                  rightSection={<IconExternalLink size={18} />}
                  component="a"
                  href="https://www.justice.gov/epstein"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    border: "none",
                  }}
                >
                  View DOJ Epstein Library
                </Button>
                <Button
                  size="lg"
                  rightSection={<IconExternalLink size={18} />}
                  component="a"
                  href="https://www.congress.gov/bill/119th-congress/house-bill/4405"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                >
                  View H.R.4405 (The Law)
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* Navigation CTAs */}
          <PageCTASection contextualAction={{ href: "/timeline", label: "View Timeline", icon: IconClock }} />
        </Container>
      </Box>
    </>
  )
}
